/* Glamora global.js: shared helpers (window.Glamora) + <slider-component>.
   Loaded with defer on every page. window.Glamora.moneyFormat / routes / strings are set
   inline in layout/theme.liquid before this file runs. */
(function () {
  'use strict';

  var G = (window.Glamora = window.Glamora || {});
  G.routes = G.routes || {};
  G.strings = G.strings || {};

  var FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled]), iframe, summary, [tabindex]:not([tabindex="-1"]), ' +
    '[contenteditable="true"]';

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* ------------------------------------------------------------------ */
  /* Money                                                               */
  /* ------------------------------------------------------------------ */

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    thousands = typeof thousands === 'string' ? thousands : ',';
    decimal = typeof decimal === 'string' ? decimal : '.';
    if (!isFinite(cents)) return '0';
    var fixed = (cents / 100).toFixed(precision);
    var parts = fixed.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    var fraction = parts[1] ? decimal + parts[1] : '';
    return whole + fraction;
  }

  /**
   * Formats cents with the shop money format (Glamora.moneyFormat).
   * Supports {{amount}}, {{amount_no_decimals}}, {{amount_with_comma_separator}},
   * {{amount_no_decimals_with_comma_separator}}, {{amount_with_apostrophe_separator}},
   * {{amount_with_space_separator}}, {{amount_no_decimals_with_space_separator}},
   * {{amount_with_period_and_space_separator}}.
   */
  G.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10);
    if (isNaN(cents)) cents = 0;
    var negative = cents < 0;
    cents = Math.abs(cents);

    var fmt = format || G.moneyFormat || '{{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = fmt.match(placeholder);
    if (!match) return fmt;

    var value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', '.');
        break;
      case 'amount':
      default:
        value = formatWithDelimiters(cents, 2, ',', '.');
        break;
    }
    var out = fmt.replace(placeholder, value);
    return negative ? '-' + out : out;
  };

  /* ------------------------------------------------------------------ */
  /* Utilities                                                           */
  /* ------------------------------------------------------------------ */

  G.debounce = function (fn, ms) {
    var timer;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(ctx, args);
      }, ms || 0);
    };
  };

  /**
   * Fetches a page through the Section Rendering API and resolves with a parsed Document.
   * Glamora.fetchSection('/collections/all?page=2', 'main-collection').then(doc => ...)
   */
  G.fetchSection = function (url, sectionId, options) {
    var target = new URL(url || window.location.href, window.location.origin);
    target.searchParams.set('section_id', sectionId);
    var init = { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } };
    if (options && options.signal) init.signal = options.signal;
    return fetch(target.toString(), init)
      .then(function (res) {
        if (!res.ok) throw new Error('Section request failed (' + res.status + ')');
        return res.text();
      })
      .then(function (html) {
        return new DOMParser().parseFromString(html, 'text/html');
      });
  };

  function isVisible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  G.getFocusable = function (root) {
    if (!root) return [];
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), function (el) {
      return isVisible(el) && !el.closest('[hidden], [inert]');
    });
  };

  /* ------------------------------------------------------------------ */
  /* Drawers                                                             */
  /* ------------------------------------------------------------------ */

  function getOverlay() {
    return document.getElementById('DrawerOverlay');
  }

  function openDrawers() {
    return Array.prototype.slice.call(document.querySelectorAll('.drawer.is-open'));
  }

  function setControllers(drawer, expanded) {
    if (!drawer.id) return;
    var selector = '[aria-controls="' + drawer.id + '"]';
    Array.prototype.forEach.call(document.querySelectorAll(selector), function (ctrl) {
      ctrl.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  function focusInto(drawer) {
    var focusable = G.getFocusable(drawer);
    var target = focusable[0];
    if (!target) {
      if (!drawer.hasAttribute('tabindex')) drawer.setAttribute('tabindex', '-1');
      target = drawer;
    }
    try {
      target.focus({ preventScroll: true });
    } catch (e) {
      target.focus();
    }
    return document.activeElement === target;
  }

  /* Focus can fail while the drawer is still switching from visibility:hidden, so retry briefly. */
  function focusWhenVisible(drawer) {
    var delays = [0, 60, 250, 700];
    (function attempt(i) {
      if (i >= delays.length) return;
      setTimeout(function () {
        if (!drawer.classList.contains('is-open')) return;
        if (drawer.contains(document.activeElement)) return;
        if (!focusInto(drawer)) attempt(i + 1);
      }, delays[i]);
    })(0);
  }

  G.openDrawer = function (el, opener) {
    if (!el) return;
    openDrawers().forEach(function (other) {
      if (other !== el) closeOne(other, false);
    });
    var active = opener || document.activeElement;
    if (!el.classList.contains('is-open') && active && active !== document.body && !el.contains(active)) {
      el._glamoraReturnFocus = active;
    }
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    setControllers(el, true);
    var overlay = getOverlay();
    if (overlay) overlay.classList.add('is-open');
    document.body.classList.add('is-locked');
    window.requestAnimationFrame(function () {
      focusWhenVisible(el);
    });
    el.dispatchEvent(new CustomEvent('glamora:drawer-open', { bubbles: true }));
  };

  function closeOne(el, restoreFocus) {
    if (!el) return;
    var hadFocus = el.contains(document.activeElement);
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    setControllers(el, false);
    var back = el._glamoraReturnFocus;
    el._glamoraReturnFocus = null;
    if (restoreFocus && hadFocus && back && back.isConnected && isVisible(back)) {
      try {
        back.focus({ preventScroll: true });
      } catch (e) {
        back.focus();
      }
    }
    el.dispatchEvent(new CustomEvent('glamora:drawer-close', { bubbles: true }));
  }

  function syncShared() {
    if (openDrawers().length) return;
    var overlay = getOverlay();
    if (overlay) overlay.classList.remove('is-open');
    document.body.classList.remove('is-locked');
  }

  /** Closes the given drawer, or every open drawer when called without an element. */
  G.closeDrawer = function (el) {
    if (el) {
      closeOne(el, true);
    } else {
      openDrawers().forEach(function (d) {
        closeOne(d, true);
      });
    }
    syncShared();
  };

  G.closeAllDrawers = function () {
    G.closeDrawer();
  };

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    if (target.id === 'DrawerOverlay') {
      G.closeAllDrawers();
      return;
    }

    var opener = target.closest('[data-drawer-open]');
    if (opener) {
      var drawer = document.getElementById(opener.getAttribute('data-drawer-open'));
      if (drawer) {
        event.preventDefault();
        G.openDrawer(drawer, opener);
      }
      return;
    }

    var closer = target.closest('[data-drawer-close]');
    if (closer) {
      var parent = closer.closest('.drawer');
      event.preventDefault();
      G.closeDrawer(parent || undefined);
    }
  });

  document.addEventListener('keydown', function (event) {
    var open = openDrawers();
    if (!open.length) return;

    if (event.key === 'Escape' || event.key === 'Esc') {
      G.closeAllDrawers();
      return;
    }

    if (event.key !== 'Tab') return;
    var drawer = open[open.length - 1];
    var focusable = G.getFocusable(drawer);
    if (!focusable.length) {
      event.preventDefault();
      focusInto(drawer);
      return;
    }
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    var current = document.activeElement;
    if (!drawer.contains(current)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && current === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && current === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* ------------------------------------------------------------------ */
  /* <slider-component>                                                  */
  /* Wraps a .slider and wires .slider-btn[data-dir="prev|next"].        */
  /* ------------------------------------------------------------------ */

  if (!customElements.get('slider-component')) {
    class SliderComponent extends HTMLElement {
      constructor() {
        super();
        this._onScroll = this._onScroll.bind(this);
        this._onClick = this._onClick.bind(this);
        this._update = this._update.bind(this);
        this._frame = null;
      }

      connectedCallback() {
        this.slider = this.querySelector('.slider');
        if (!this.slider || this._connected) return;
        this._connected = true;
        this.slider.addEventListener('scroll', this._onScroll, { passive: true });
        this.addEventListener('click', this._onClick);
        if ('ResizeObserver' in window) {
          this._resizeObserver = new ResizeObserver(this._onScroll);
          this._resizeObserver.observe(this.slider);
        } else {
          this._onResize = G.debounce(this._update, 150);
          window.addEventListener('resize', this._onResize);
        }
        this._update();
      }

      disconnectedCallback() {
        this._connected = false;
        if (this.slider) this.slider.removeEventListener('scroll', this._onScroll);
        this.removeEventListener('click', this._onClick);
        if (this._resizeObserver) this._resizeObserver.disconnect();
        if (this._onResize) window.removeEventListener('resize', this._onResize);
        if (this._frame) window.cancelAnimationFrame(this._frame);
      }

      get slides() {
        return Array.prototype.filter.call(this.slider.children, isVisible);
      }

      get isRtl() {
        return window.getComputedStyle(this.slider).direction === 'rtl';
      }

      _onScroll() {
        if (this._frame) return;
        this._frame = window.requestAnimationFrame(
          function () {
            this._frame = null;
            this._update();
          }.bind(this)
        );
      }

      _onClick(event) {
        var target = event.target instanceof Element ? event.target : null;
        var btn = target && target.closest('.slider-btn[data-dir]');
        if (!btn || btn.closest('slider-component') !== this) return;
        event.preventDefault();
        this.go(btn.getAttribute('data-dir') === 'prev' ? -1 : 1);
      }

      step() {
        var slides = this.slides;
        if (!slides.length) return this.slider.clientWidth;
        var gap = parseFloat(window.getComputedStyle(this.slider).columnGap) || 0;
        return slides[0].getBoundingClientRect().width + gap;
      }

      go(direction) {
        if (!this.slider) return;
        var amount = this.step() * direction * (this.isRtl ? -1 : 1);
        this.slider.scrollBy({ left: amount, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      }

      _update() {
        if (!this.slider) return;
        var max = this.slider.scrollWidth - this.slider.clientWidth;
        var position = Math.abs(this.slider.scrollLeft);
        var noOverflow = max <= 2;
        var atStart = position <= 2;
        var atEnd = position >= max - 2;
        this.classList.toggle('is-static', noOverflow);
        Array.prototype.forEach.call(this.querySelectorAll('.slider-btn[data-dir="prev"]'), function (b) {
          b.disabled = noOverflow || atStart;
        });
        Array.prototype.forEach.call(this.querySelectorAll('.slider-btn[data-dir="next"]'), function (b) {
          b.disabled = noOverflow || atEnd;
        });
      }
    }

    customElements.define('slider-component', SliderComponent);
  }

  /* ------------------------------------------------------------------ */
  /* Marquee fill                                                        */
  /* .marquee > .marquee__track holds its content twice and scrolls      */
  /* translateX(0 -> -50%) (base.css). When one copy is narrower than    */
  /* the bar, the loop would show a gap, so the whole set is cloned      */
  /* (keeps it periodic) and the duration is scaled to keep the speed.   */
  /* Opt out with data-marquee-nofill on .marquee.                      */
  /* ------------------------------------------------------------------ */

  function parseDuration(value) {
    var first = String(value || '').split(',')[0].trim();
    var n = parseFloat(first);
    if (!isFinite(n) || n <= 0) return 0;
    return /ms$/.test(first) ? n / 1000 : n;
  }

  function marqueeClone(node) {
    var clone = node.cloneNode(true);
    if (clone.nodeType !== 1) return clone;
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('data-marquee-clone', '');
    clone.removeAttribute('id');
    Array.prototype.forEach.call(clone.querySelectorAll('[id]'), function (el) {
      el.removeAttribute('id');
    });
    if (clone.matches(FOCUSABLE)) clone.setAttribute('tabindex', '-1');
    Array.prototype.forEach.call(clone.querySelectorAll(FOCUSABLE), function (el) {
      el.setAttribute('tabindex', '-1');
    });
    return clone;
  }

  G.fillMarquee = function (marquee) {
    if (!marquee || marquee.hasAttribute('data-marquee-nofill')) return;
    var track = marquee.querySelector('.marquee__track');
    if (!track || !track.children.length) return;
    var style = window.getComputedStyle(track);
    if (!style.animationName || style.animationName === 'none') return;
    var available = marquee.clientWidth;
    if (!available || !track.offsetWidth) return;

    if (!track._glamoraBaseDuration) track._glamoraBaseDuration = parseDuration(style.animationDuration);
    var factor = track._glamoraFactor || 1;
    var rounds = 0;
    while (track.offsetWidth / 2 < available && rounds < 4) {
      var originals = Array.prototype.slice.call(track.children);
      var fragment = document.createDocumentFragment();
      originals.forEach(function (child) {
        fragment.appendChild(marqueeClone(child));
      });
      track.appendChild(fragment);
      factor *= 2;
      rounds += 1;
    }
    if (rounds && track._glamoraBaseDuration) {
      track._glamoraFactor = factor;
      track.style.animationDuration = track._glamoraBaseDuration * factor + 's';
    }
  };

  G.initMarquees = function (root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll('.marquee'), G.fillMarquee);
  };

  G.initMarquees();
  window.addEventListener('load', function () {
    G.initMarquees();
  });
  window.addEventListener('resize', G.debounce(function () {
    G.initMarquees();
  }, 200));
  document.addEventListener('shopify:section:load', function (event) {
    G.initMarquees(event.target);
  });

  /* ------------------------------------------------------------------ */
  /* Motion toggle (WCAG 2.2.2: pause, stop, hide)                       */
  /* <button data-motion-toggle aria-controls="<.marquee id>"            */
  /*   aria-pressed="false" [data-motion-key="announcement"]>            */
  /* (snippets/motion-toggle.liquid). A click toggles .is-user-paused on */
  /* the target (base.css pauses its track) and aria-pressed on every    */
  /* toggle for that target or key. A data-motion-key remembers the      */
  /* choice on this device (localStorage) for the next pages.            */
  /* html.has-motion-toggle reveals the buttons only once this runs, so  */
  /* a page without this script never shows a button that does nothing. */
  /* ------------------------------------------------------------------ */

  var MOTION_STORE = 'glamora:motion-paused';

  function readPausedKeys() {
    try {
      var list = JSON.parse(window.localStorage.getItem(MOTION_STORE) || '[]');
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  function storePausedKey(key, paused) {
    if (!key) return;
    try {
      var list = readPausedKeys().filter(function (k) {
        return k !== key;
      });
      if (paused) list.push(key);
      if (list.length) {
        window.localStorage.setItem(MOTION_STORE, JSON.stringify(list));
      } else {
        window.localStorage.removeItem(MOTION_STORE);
      }
    } catch (e) {
      /* Storage blocked: the choice still holds for this page. */
    }
  }

  function motionTarget(toggle) {
    var id = toggle.getAttribute('aria-controls') || toggle.getAttribute('data-motion-target');
    var target = id ? document.getElementById(id) : null;
    if (target) return target;
    var inside = toggle.closest('.marquee');
    if (inside) return inside;
    return toggle.parentElement ? toggle.parentElement.querySelector('.marquee') : null;
  }

  function applyMotionState(toggle, paused) {
    var target = motionTarget(toggle);
    if (target) target.classList.toggle('is-user-paused', paused);
    toggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
  }

  /** Pauses (true) or resumes (false) the content a toggle controls, plus every toggle sharing its key or target. */
  G.setMotionPaused = function (toggle, paused) {
    if (!toggle) return;
    var key = toggle.getAttribute('data-motion-key');
    var target = motionTarget(toggle);
    Array.prototype.forEach.call(document.querySelectorAll('[data-motion-toggle]'), function (other) {
      if (other === toggle || (key && other.getAttribute('data-motion-key') === key) || (target && motionTarget(other) === target)) {
        applyMotionState(other, paused);
      }
    });
    storePausedKey(key, paused);
  };

  G.initMotionToggles = function (root) {
    var scope = root && root.querySelectorAll ? root : document;
    var paused = readPausedKeys();
    var toggles = Array.prototype.slice.call(scope.querySelectorAll('[data-motion-toggle]'));
    if (scope.matches && scope.matches('[data-motion-toggle]')) toggles.push(scope);
    toggles.forEach(function (toggle) {
      var key = toggle.getAttribute('data-motion-key');
      if (key && paused.indexOf(key) !== -1) applyMotionState(toggle, true);
    });
  };

  document.addEventListener('click', function (event) {
    var toggle = event.target instanceof Element ? event.target.closest('[data-motion-toggle]') : null;
    if (!toggle) return;
    event.preventDefault();
    G.setMotionPaused(toggle, toggle.getAttribute('aria-pressed') !== 'true');
  });

  document.documentElement.classList.add('has-motion-toggle');
  G.initMotionToggles();
  document.addEventListener('shopify:section:load', function (event) {
    G.initMotionToggles(event.target);
  });
})();
